import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import ProductCard from "../components/ProductCard";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const filtered = useMemo(() => {
    const list =
      activeCat === "all"
        ? products
        : products.filter((p) => p.categories.includes(activeCat));
    const sorted = [...list];
    if (sort === "price-asc") sorted.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") sorted.sort((a, b) => b.price - a.price);
    else if (sort === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    else sorted.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return sorted;
  }, [products, activeCat, sort]);

  return (
    <div data-testid="shop-page" className="animate-in fade-in duration-500">
      <section className="px-6 md:px-12 lg:px-24 pt-20 md:pt-28 pb-12 border-b border-border">
        <p className="overline text-primary mb-5">The catalog</p>
        <h1 className="editorial-heading text-5xl md:text-7xl text-secondary leading-[0.95] mb-6">
          Every peptide,<br />
          <em className="italic text-primary">all in one place.</em>
        </h1>
        <p className="text-muted-foreground max-w-2xl text-base leading-relaxed">
          Browse our full selection of research-grade peptides. Filter by category or
          sort by price to find what your work needs.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-b border-border">
        <div className="flex flex-wrap gap-2" data-testid="shop-category-filters">
          <button
            onClick={() => setActiveCat("all")}
            data-testid="shop-filter-all"
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
              activeCat === "all"
                ? "bg-secondary text-secondary-foreground border-secondary"
                : "border-border text-secondary hover:border-secondary"
            }`}
          >
            All ({products.length})
          </button>
          {CATEGORIES.map((c) => {
            const count = products.filter((p) => p.categories.includes(c.slug)).length;
            return (
              <button
                key={c.slug}
                onClick={() => setActiveCat(c.slug)}
                data-testid={`shop-filter-${c.slug}`}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                  activeCat === c.slug
                    ? "bg-secondary text-secondary-foreground border-secondary"
                    : "border-border text-secondary hover:border-secondary"
                }`}
              >
                {c.short} ({count})
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]">
          <span className="text-muted-foreground hidden md:inline">Sort</span>
          {[
            { v: "featured", l: "Featured" },
            { v: "price-asc", l: "Price ↑" },
            { v: "price-desc", l: "Price ↓" },
            { v: "name", l: "A–Z" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setSort(opt.v)}
              data-testid={`shop-sort-${opt.v}`}
              className={`px-3 py-1.5 transition-colors ${
                sort === opt.v
                  ? "text-secondary border-b border-secondary"
                  : "text-muted-foreground hover:text-secondary"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20">
        {filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-20">
            No products match this filter.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16 border-t border-border">
        <p className="text-xs text-muted-foreground/80 max-w-3xl leading-relaxed">
          Disclaimer — all compounds shown are sold strictly for laboratory research and in
          vitro experimentation. They are not approved for human or veterinary consumption,
          diagnosis, treatment, or any therapeutic use.
        </p>
      </section>
    </div>
  );
}
